import {
  InfoTip,
  InputField,
  InputItem,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import { useEffect, useState } from 'react';
import { Control, FormState, UseFormRegister } from 'react-hook-form';

import {
  SolutionCategory,
  SolutionInputFields,
} from '../../common/api/models/solution';
import { readCategoryOptions } from '../../common/api/solution';
import { ToggleButton } from '../../common/components/ToggleButton';

interface SolutionRegistrationInputProps {
  register: UseFormRegister<SolutionInputFields>;
  control: Control<SolutionInputFields, any>;
  formState: FormState<SolutionInputFields>;
  disabled?: boolean;
  createButNotEditFields?: string[];
}

export const SolutionRegistrationInput = ({
  register,
  control,
  formState,
  disabled = false,
  createButNotEditFields = [],
}: SolutionRegistrationInputProps) => {
  const [categoryOptions, setCategoryOptions] = useState<SolutionCategory[]>(
    []
  );

  if (
    formState.defaultValues?.visibility &&
    formState.defaultValues.visibility?.isMarketplaceVisible === undefined
  ) {
    formState.defaultValues.visibility.isMarketplaceVisible = false;
  }

  useEffect(() => {
    const executeQuery = async () => {
      const categories = await readCategoryOptions();
      setCategoryOptions(categories);
    };
    executeQuery();
  }, []);

  return (
    <>
      <div className="w-full flex flex-col gap-3">
        <InputField
          className="w-full"
          disabled={disabled}
          errors={formState.errors}
          inputKey="displayName"
          placeholder="Enter the Solution Name"
          register={register}
          registerProps={{
            required: {
              value: true,
              message: 'Solution name must be 2 to 50 characters.',
            },
            pattern: {
              value: /^(?![\s-])[\sa-zA-Z0-9/#@_-]+$/,
              message:
                'Solution name can contain only letters and numbers, plus special characters / # @ _ -',
            },
            validate: (value) => {
              if (!value) {
                return 'Solution name is required';
              } else if (
                value.toString().length < 2 ||
                value.toString().length > 50
              ) {
                return 'Solution name must be 2 to 50 characters';
              }
              return true;
            },
          }}
          testId="displayName-input"
          title="Solution Name"
          validationMessage="Solution name must be 2 to 50 characters."
        />

        <InputField
          className="w-full"
          disabled={disabled}
          errors={formState.errors}
          inputKey="shortDescription"
          placeholder="Describe your Solution"
          register={register}
          registerProps={{
            required: {
              value: true,
              message: 'Enter a description between 15 and 250 characters.',
            },
            minLength: {
              value: 15,
              message: 'Enter at least 15 characters.',
            },
            maxLength: {
              value: 250,
              message: 'Enter a description of 250 characters or fewer.',
            },
          }}
          testId="shortDescription-input"
          title="Solution Description"
          validationMessage="Enter a description between 15 and 250 characters."
        />

        <InputItem title="Category">
          <fieldset>
            <div className="text-xs pb-3">(Select all that apply)</div>
            {categoryOptions &&
              categoryOptions.map((option) => (
                <div className="form-check" key={option.id}>
                  <input
                    className={`${
                      disabled ? 'cursor-auto' : 'cursor-pointer'
                    } form-check-input appearance-none h-4 w-4 border border-gray-300 rounded-sm bg-white text-brand-blue focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2`}
                    data-testid={option.id}
                    disabled={disabled}
                    id={option.id}
                    type="checkbox"
                    value={option.id}
                    {...register('categories', {
                      required: {
                        value: true,
                        message: 'Select a category.',
                      },
                    })}
                  />
                  <label
                    className="form-check-label inline-block text-gray-800"
                    htmlFor={option.id}
                  >
                    {option.displayName}
                  </label>
                </div>
              ))}
            {formState.errors.categories && (
              <div className="text-xs mt-2 text-red-600 dark:text-red-400 left-0">
                {formState.errors.categories.message}
              </div>
            )}
          </fieldset>
        </InputItem>
      </div>
      <div className="w-full flex flex-col gap-3 pb-3">
        {formState.defaultValues &&
          formState.defaultValues.visibility?.isMarketplaceVisible !==
            undefined && (
            <ToggleButton
              className="w-90"
              control={control}
              defaultValue={
                formState.defaultValues.visibility.isMarketplaceVisible
              }
              disabled={disabled}
              errors={formState.errors}
              inputKey="visibility.isMarketplaceVisible"
              offValue="Internal"
              onValue="External"
              register={register}
              title={
                <span className="flex flex-row gap-2">
                  <span>Visibility</span>
                  <InfoTip
                    id="visibility"
                    tip={{
                      title: 'Visibility',
                      content:
                        'Choose externally available only if you would like to publish your solution to the marketplace.',
                    }}
                  />
                </span>
              }
            />
          )}

        {!createButNotEditFields.includes('version') && (
          <InputField
            className="w-full"
            disabled={disabled}
            errors={formState.errors}
            inputKey="version"
            placeholder="Enter Version e.g. 1.0.4"
            register={register}
            registerProps={{
              required: {
                value: true,
                message: 'Enter a version.',
              },
              pattern: {
                value:
                  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/,
                message: 'Make sure version is compatbile with semver.',
              },
            }}
            testId="version-input"
            title="Version"
          />
        )}
      </div>
    </>
  );
};
